/* global APP_VERSION, REACT_APP_PUBLIC_SENTRY_ENVIRONMENT */

import { EventParams } from './types';

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let initialized = false;

// Medición del PANEL (`G-CF05DPFXXE`). Se retiene para poder dirigir a ELLA los eventos del
// panel con `send_to`. Sin eso, `gtag('event', …)` sin destino va a TODOS los destinos
// configurados, y `/registro` configura además la propiedad de MARKETING — así que cada
// page_view del panel tras el auto-login (que navega por SPA, sin recargar) terminaba también
// en marketing, con las `user_properties` del comercio adheridas (`gtag('set')` es global y no
// acepta `send_to`). Verificado en pruebas el 2026-08-30: `/control-pedidos` llegando a
// `G-FW6FJS4KRP` con `uid`, `plan_id=PROFESIONAL`, `business_type`.
let panelMeasurementId: string | null = null;

const isDev = (): boolean => {
  try {
    return REACT_APP_PUBLIC_SENTRY_ENVIRONMENT !== 'production';
  } catch {
    return true;
  }
};

const appVersion = (): string => {
  try {
    return APP_VERSION;
  } catch {
    return '';
  }
};

const ensureGtag = (): void => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      // gtag.js espera el objeto `arguments` (no un array) en el dataLayer.
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
};

// Consent Mode v2 — estado por defecto. Se setea ANTES de cargar gtag.
// Supuesto: panel B2B autenticado → analytics permitido; publicidad denegada.
// Marcar revisión legal antes de prod si aplica aviso de cookies.
export const setConsentDefault = (): void => {
  ensureGtag();
  if (typeof window === 'undefined' || !window.gtag) return;
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
};

export const initGA4 = (measurementId: string): void => {
  if (initialized || typeof window === 'undefined' || !measurementId) return;
  initialized = true;
  panelMeasurementId = measurementId;
  ensureGtag();

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false, // pageviews SPA se mandan manualmente
    debug_mode: isDev(),
    app_version: appVersion(),
  });
};

// Evento del PANEL. Va dirigido a la medición del panel con `send_to`: sin él, gtag lo manda a
// TODOS los destinos configurados — incluida la propiedad de marketing que `/registro` configura
// — y la telemetría de producto termina contaminando el embudo de marketing.
export const ga4Event = (name: string, params?: EventParams): void => {
  if (!initialized || typeof window === 'undefined' || !window.gtag) return;
  try {
    const scoped = panelMeasurementId ? { ...params, send_to: panelMeasurementId } : params || {};
    window.gtag('event', name, scoped);
  } catch {
    // noop — no romper la cadena de tracking
  }
};

// Evento a una medición GA4 concreta vía `send_to` (p.ej. la propiedad de marketing) sin
// configurarla global: los pageviews/eventos del panel NO se espejan a ese destino, gtag.js
// carga el target solo para este evento.
export const ga4EventTo = (targetId: string, name: string, params?: EventParams): void => {
  if (!initialized || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('event', name, { ...params, send_to: targetId });
  } catch {
    // noop — no romper la cadena de tracking
  }
};

// Page view dirigido a UNA medición concreta. Existe para que la propiedad de marketing pueda
// registrar la visita a `/registro` SIN dejar `send_page_view: true`, que haría que gtag mandara
// un page_view automático a ese destino en CADA cambio de ruta de la SPA — incluidas las rutas
// del panel tras el auto-login, y con las `user_properties` del comercio adheridas (`gtag('set')`
// es global y no acepta `send_to`). Un page_view explícito y acotado dice lo mismo sin la fuga.
export const ga4PageViewTo = (
  targetId: string,
  params: { page_path?: string; page_location?: string; page_title?: string },
): void => {
  if (typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('event', 'page_view', { ...params, send_to: targetId });
  } catch {
    // noop — no romper la cadena de tracking
  }
};

// Page view del PANEL. Mismo criterio que `ga4Event`: dirigido con `send_to`. Este era el que
// filtraba `/onboarding` y `/control-pedidos` a marketing tras el auto-login.
export const ga4PageView = (params: { page_path?: string; page_location?: string; page_title?: string }): void => {
  if (!initialized || typeof window === 'undefined' || !window.gtag) return;
  try {
    const scoped = panelMeasurementId ? { ...params, send_to: panelMeasurementId } : params;
    window.gtag('event', 'page_view', scoped);
  } catch {
    // noop
  }
};

// Interruptor POR PROPIEDAD de gtag.js: `window['ga-disable-<MEASUREMENT_ID>']`. gtag lo consulta
// en CADA hit, así que apaga también lo que ninguna opción de `config` gobierna — en concreto el
// *enhanced measurement*, que es un ajuste del STREAM en el admin de GA4, no del código.
//
// Por qué hace falta: `send_page_view:false` solo suprime el page_view que gtag manda al correr el
// `config`. El stream de MARKETING tiene encendido "cambios de página por eventos de historial",
// así que cada `pushState` de la SPA le mandaba un page_view — y con las `user_properties` del
// comercio adheridas, porque `gtag('set','user_properties',…)` es global y no acepta `send_to`.
// El stream del PANEL lo tiene apagado, por eso ese destino nunca mostró el síntoma.
//
// Medido en pruebas el 2026-08-31 sobre el build desplegado, con control de presencia:
//   sin flag  → 1 pushState = +1 hit a marketing, +0 al panel
//   con flag  → 2 pushState = +0 hits a marketing, +1 al panel  (el panel sigue midiendo)
export const ga4SetDestinationEnabled = (targetId: string, enabled: boolean): void => {
  if (typeof window === 'undefined' || !targetId) return;
  (window as unknown as Record<string, boolean>)[`ga-disable-${targetId}`] = !enabled;
};

export const ga4SetUser = (userId: string, userProps: EventParams): void => {
  if (!initialized || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('set', { user_id: userId });
    window.gtag('set', 'user_properties', userProps);
  } catch {
    // noop
  }
};

// Mergea user_properties sin re-identificar al usuario. gtag('set','user_properties',…)
// es incremental, así que estas dimensiones genéricas del framework de rollout
// (rollout_feature/rollout_variant/rollout_state) se suman a las que ya fijó `ga4SetUser` y
// viajan en los eventos siguientes. Cada dimensión user-scoped se registra como custom
// dimension en el admin de GA4 (Configurar → Definiciones personalizadas) — el SDK solo
// las emite; el slicer `rollout_feature` permite separar 2+ features bajo las mismas claves.
export const ga4SetUserProperties = (userProps: EventParams): void => {
  if (!initialized || typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('set', 'user_properties', userProps);
  } catch {
    // noop
  }
};

export const setConsent = (update: Record<string, 'granted' | 'denied'>): void => {
  if (typeof window === 'undefined' || !window.gtag) return;
  try {
    window.gtag('consent', 'update', update);
  } catch {
    // noop
  }
};
